import { createDecorator } from 'components/shared/edit/decorators/focusOnErrorsCustom';
import EnumDropdown from 'components/shared/edit/fields/EnumDropdown';
import Spinner from 'components/shared/Spinner';
import React, { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { Alert, Button } from 'reactstrap';
import AxiosHelper from 'services/AxiosHelper';
import { handleAxiosErrorWithValidation } from 'util/Misc';
import Input from '../shared/Input';

const focusOnErrors = createDecorator();

interface RegisterInstallationData {
	canSkipRegistration: boolean;
	hasKey: boolean;
}

enum RegistrationEnvironment {
	PreProduction = 'Pre-Production',
	Production = 'Production',
}

interface FormData {
	licenseKey: string;
	registrationEnvironment: RegistrationEnvironment;
}

interface RegistrationResponse {
	message?: string;
	redirectUrl?: string;
	success: boolean;
}

const redirectToAdmin = () => window.location.replace('/');

export default function RegisterInstallation(props: Readonly<any>) {
	const searchParams = new URLSearchParams(props.location ? props.location.search : '');
	const redirectUrl = searchParams.get('ReturnUrl');
	const [registrationWarnings, setRegistrationWarnings] = useState('');

	const submit = async (formValues: FormData) => {
		const url = `/ajax/register-installation/`;

		try {
			const response = await AxiosHelper.post<RegistrationResponse>(url, formValues);
			if (response.data.success) {
				// No need to show any messages if there are none
				if (!response.data.message?.trim()) {
					window.location.href = redirectUrl ? redirectUrl : '/';
					return;
				}

				setRegistrationWarnings(response.data.message);
				return;
			} else {
				window.location.href = response.data.redirectUrl ? response.data.redirectUrl : '/';
			}
			return;
		} catch (error) {
			return handleAxiosErrorWithValidation(error, 'An error occurred while submitting.');
		}
	};

	const registerData: RegisterInstallationData = (window as any).__ROC_INSTALLATION_DATA__;
	const { canSkipRegistration, hasKey } = registerData;

	return registrationWarnings ? (
		<div className="login-form">
			<div>
				<Alert color="warning">{registrationWarnings}</Alert>
				<div className="login-form__actions form-group">
					<Button className="login-form__action" data-testid="acknowledge-errors" onClick={redirectToAdmin}>
						OK
					</Button>
				</div>
			</div>
		</div>
	) : (
		<div className="login-form">
			<div>
				{!hasKey ? (
					<h2 className="login-form-heading">
						Your ROC installation needs to be registered before you can proceed. Please enter your license
						key below and select your installation environment.
					</h2>
				) : (
					<h2 className="login-form-heading">
						A ROC license key has already been entered. You may enter a new one below if you wish to change
						it.
					</h2>
				)}
			</div>

			<Form
				onSubmit={submit}
				decorators={[focusOnErrors]}
				subscription={{ submitting: true, submitError: true }}
				render={({ handleSubmit, submitError, submitting }) => (
					<form onSubmit={handleSubmit} className="login-form">
						{submitError ? (
							<div>
								<Alert color="danger">
									<strong>There was a problem registering this ROC installation:</strong>
									<br />
									{Array.isArray(submitError)
										? submitError.map((singleError) => (
												// Display all errors on their own lines
												<>
													{singleError}
													<br />
												</>
										  ))
										: submitError}
								</Alert>
							</div>
						) : null}

						<Field name="licenseKey" label="License key" type="text" autoFocus component={Input} />
						<Field
							name="registrationEnvironment"
							component={EnumDropdown}
							enumType={RegistrationEnvironment}
							addSelectOption
						/>
						<div className="login-form__actions form-group">
							<Button
								type="submit"
								disabled={submitting}
								color="primary"
								data-testid="submit-2fa-login-btn"
								className="login-form__action login-form__submit-btn"
							>
								Register
							</Button>
							{canSkipRegistration ? (
								<>
									<Button
										className="login-form__action"
										data-testid="skip-register-installation-setup"
										onClick={redirectToAdmin}
									>
										Skip
									</Button>
								</>
							) : null}
						</div>

						<div className="login-form__actions form-group">{submitting ? <Spinner /> : null}</div>
					</form>
				)}
			/>
		</div>
	);
}
